// Client-side cache of the user's clients, activities and time entries.
//
// Every page reads from here, so navigating is instant and numbers agree
// across pages. The cache is filled from the API, kept in localStorage for an
// instant first paint (stale-while-revalidate) and updated in place after
// every change this app makes. Changes made elsewhere (the iOS app) are picked
// up when the window regains focus.

import { api } from '../api';
import { session } from './session.svelte';
import type { Activity, Client, TimeEntry, PaymentStatus } from '../types';

const CACHE_KEY = 'tp.cache.v1';
const STALE_MS = 30_000;

function sortEntries(list: TimeEntry[]): TimeEntry[] {
  return list.sort((a, b) => (b.start_time || '').localeCompare(a.start_time || '') || b.id - a.id);
}

export interface EntryInput {
  date: string;
  start_time: string; // HH:MM
  end_time: string; // HH:MM
  client_id: number | null;
  activity_id?: number | null;
  description?: string | null;
  rate?: number | null;
  billable?: boolean;
  pause_minutes?: number;
  payment_status?: PaymentStatus;
  invoice_reference?: string | null;
  amount_paid?: number;
}

class DataStore {
  clients = $state<Client[]>([]);
  activities = $state<Activity[]>([]);
  entries = $state<TimeEntry[]>([]);
  status = $state<'idle' | 'loading' | 'ready' | 'error'>('idle');
  error = $state<string | null>(null);
  /** Bumped on every change; use it to recompute expensive derived data. */
  version = $state(0);

  clientById = $derived(new Map(this.clients.map((c) => [c.id, c])));
  activityById = $derived(new Map(this.activities.map((a) => [a.id, a])));
  activeClients = $derived(this.clients.filter((c) => c.active !== false));

  private loadedAt = 0;
  private inflight: Promise<void> | null = null;

  constructor() {
    if (typeof window === 'undefined') return;
    window.addEventListener('focus', () => {
      if (session.signedIn && Date.now() - this.loadedAt > STALE_MS) this.refresh().catch(() => {});
    });
    session.onSignedOut(() => this.reset());
  }

  private reset() {
    this.clients = [];
    this.activities = [];
    this.entries = [];
    this.status = 'idle';
    this.loadedAt = 0;
    this.version += 1;
  }

  /** Paint from the localStorage copy (if it belongs to this user). */
  hydrate() {
    try {
      const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!raw || raw.userId !== session.user?.id) return;
      this.clients = raw.clients || [];
      this.activities = raw.activities || [];
      this.entries = raw.entries || [];
      if (this.status === 'idle') this.status = 'ready';
      this.version += 1;
    } catch { /* ignore a broken cache */ }
  }

  private persist() {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        userId: session.user?.id,
        at: Date.now(),
        clients: $state.snapshot(this.clients),
        activities: $state.snapshot(this.activities),
        entries: $state.snapshot(this.entries)
      }));
    } catch { /* quota: fine, the API is the source of truth */ }
  }

  private changed() {
    this.version += 1;
    this.persist();
  }

  /** Load once, or again when older than maxAgeMs. */
  ensure(maxAgeMs = STALE_MS): Promise<void> {
    if (this.status === 'ready' && Date.now() - this.loadedAt < maxAgeMs) return Promise.resolve();
    return this.refresh();
  }

  refresh(): Promise<void> {
    if (this.inflight) return this.inflight;
    if (this.status !== 'ready') this.status = 'loading';
    this.inflight = (async () => {
      try {
        const [clients, activities, entries] = await Promise.all([
          api.get<Client[]>('/api/clients', { query: { include_inactive: 1 } }),
          api.get<Activity[]>('/api/activities'),
          api.get<TimeEntry[]>('/api/time-entries-all')
        ]);
        this.clients = clients || [];
        this.activities = activities || [];
        this.entries = sortEntries(entries || []);
        this.status = 'ready';
        this.error = null;
        this.loadedAt = Date.now();
        this.changed();
      } catch (err) {
        if (this.status !== 'ready') this.status = 'error';
        this.error = (err as Error).message;
        throw err;
      } finally {
        this.inflight = null;
      }
    })();
    return this.inflight;
  }

  async refreshEntries() {
    const entries = await api.get<TimeEntry[]>('/api/time-entries-all');
    this.entries = sortEntries(entries || []);
    this.changed();
  }

  async refreshClients() {
    this.clients = (await api.get<Client[]>('/api/clients', { query: { include_inactive: 1 } })) || [];
    this.changed();
  }

  async refreshActivities() {
    this.activities = (await api.get<Activity[]>('/api/activities')) || [];
    this.changed();
  }

  activitiesFor(clientId: number | null | undefined): Activity[] {
    return this.activities.filter((a) => a.active !== false && (a.client_id === null || a.client_id === clientId));
  }

  // ---------- Clients ----------

  async createClient(details: Partial<Client> & { name: string }): Promise<Client> {
    const res = await api.post<{ id: number; client: Client }>('/api/clients', { name: details.name });
    let client = res.client;
    const extra = { ...details } as Record<string, unknown>;
    delete extra.name;
    if (Object.keys(extra).length) {
      client = await api.put<Client>(`/api/clients/${res.id}`, { name: client.name, ...extra });
    }
    this.clients = [...this.clients.filter((c) => c.id !== client.id), client].sort((a, b) => a.name.localeCompare(b.name));
    this.changed();
    return client;
  }

  async updateClient(id: number, patch: Partial<Client>): Promise<Client> {
    const current = this.clientById.get(id);
    const body = { name: current?.name, ...patch };
    const client = await api.put<Client>(`/api/clients/${id}`, body);
    this.clients = this.clients.map((c) => (c.id === id ? { ...c, ...client } : c));
    // Entry rows carry the client name.
    if (patch.name) this.entries = this.entries.map((e) => (e.client_id === id ? { ...e, client_name: client.name } : e));
    this.changed();
    return client;
  }

  async deleteClient(id: number) {
    await api.del(`/api/clients/${id}`);
    this.clients = this.clients.filter((c) => c.id !== id);
    this.activities = this.activities.filter((a) => a.client_id !== id);
    this.entries = this.entries.filter((e) => e.client_id !== id);
    this.changed();
  }

  async migrateClientEntries(id: number, targetClientId: number) {
    const res = await api.post(`/api/clients/${id}/entries/migrate`, { targetClientId, scope: 'all', copyActivities: true });
    await Promise.all([this.refreshEntries(), this.refreshActivities()]);
    return res;
  }

  async deleteClientEntries(id: number) {
    const res = await api.post(`/api/clients/${id}/entries/delete`, { scope: 'all' });
    this.entries = this.entries.filter((e) => e.client_id !== id);
    this.changed();
    return res;
  }

  async recalculateRounding(id: number) {
    const res = await api.post(`/api/clients/${id}/recalculate-rounding`);
    await this.refreshEntries();
    return res;
  }

  // ---------- Activities ----------

  async createActivity(input: { name: string; client_id: number | null; default_rate?: number | null; quote_price?: number | null; description?: string | null }): Promise<Activity> {
    let activity: Activity;
    try {
      activity = await api.post<Activity>('/api/activities', {
        name: input.name,
        client_id: input.client_id,
        default_rate: input.default_rate ?? null,
        description: input.description ?? null
      });
    } catch (err) {
      // 409: an activity with this name exists — reuse it.
      const existing = (err as { status?: number; body?: { existing?: Activity } });
      if (existing.status === 409 && existing.body?.existing) activity = existing.body.existing;
      else throw err;
    }
    if (input.quote_price !== undefined && input.quote_price !== null) {
      const res = await api.put<{ activity: Activity }>(`/api/activities/${activity.id}`, { quote_price: input.quote_price });
      activity = res.activity || { ...activity, quote_price: input.quote_price };
    }
    this.activities = [...this.activities.filter((a) => a.id !== activity.id), activity];
    this.changed();
    return activity;
  }

  async updateActivity(id: number, patch: { name?: string; quote_price?: number | null; default_rate?: number | null }): Promise<Activity> {
    const res = await api.put<{ activity: Activity }>(`/api/activities/${id}`, patch);
    const activity = res.activity;
    this.activities = this.activities.map((a) => (a.id === id ? { ...a, ...activity } : a));
    if (patch.name) this.entries = this.entries.map((e) => (e.activity_id === id ? { ...e, activity_name: activity.name } : e));
    this.changed();
    return activity;
  }

  async deleteActivity(id: number) {
    await api.del(`/api/activities/${id}`);
    this.activities = this.activities.filter((a) => a.id !== id);
    this.entries = this.entries.map((e) => (e.activity_id === id ? { ...e, activity_id: null, activity_name: null } : e));
    this.changed();
  }

  // ---------- Entries ----------

  private upsertEntry(entry: TimeEntry) {
    const others = this.entries.filter((e) => e.id !== entry.id);
    this.entries = sortEntries([entry, ...others]);
    this.changed();
  }

  async createEntry(input: EntryInput): Promise<TimeEntry> {
    const res = await api.post<{ id: number }>('/api/time-entries', { billable: true, pause_minutes: 0, ...input });
    const entry = await api.get<TimeEntry>(`/api/time-entries/${res.id}`);
    this.upsertEntry(entry);
    return entry;
  }

  async updateEntry(id: number, patch: Partial<EntryInput> & { amount_paid?: number; paid_at?: string | null }): Promise<TimeEntry> {
    const res = await api.put<{ success: boolean; entry: TimeEntry }>(`/api/time-entries/${id}`, patch);
    // The PUT response lacks the joined client/activity names: reload the row.
    const entry = await api.get<TimeEntry>(`/api/time-entries/${id}`).catch(() => res.entry);
    this.upsertEntry(entry);
    return entry;
  }

  async deleteEntry(id: number) {
    const before = this.entries;
    this.entries = this.entries.filter((e) => e.id !== id);
    this.changed();
    try {
      await api.del(`/api/time-entries/${id}`);
    } catch (err) {
      this.entries = before;
      this.changed();
      throw err;
    }
  }

  async deleteEntries(ids: number[]) {
    if (!ids.length) return;
    const before = this.entries;
    const set = new Set(ids);
    this.entries = this.entries.filter((e) => !set.has(e.id));
    this.changed();
    try {
      await api.del('/api/time-entries/bulk-delete', { ids });
    } catch (err) {
      this.entries = before;
      this.changed();
      throw err;
    }
  }

  async recordPayment(entryIds: number[], status: 'open' | 'partial' | 'paid' | 'non_billable', opts: { amount?: number; proportional?: boolean; invoiceReference?: string } = {}) {
    const res = await api.post<{ entries?: TimeEntry[] }>('/api/time-entries/record-payment', {
      entryIds,
      status,
      amount: opts.amount,
      proportional: opts.proportional ?? true,
      invoiceReference: opts.invoiceReference
    });
    if (Array.isArray(res.entries) && res.entries.length) {
      const byId = new Map(res.entries.map((e) => [e.id, e]));
      this.entries = this.entries.map((e) => (byId.has(e.id) ? { ...e, ...byId.get(e.id)!, client_name: e.client_name, activity_name: e.activity_name } : e));
      this.changed();
    } else {
      await this.refreshEntries();
    }
    return res;
  }

  async bulkUpdateClient(ids: number[], clientId: number) {
    await api.put('/api/time-entries/bulk-update-client', { ids, clientId });
    await this.refreshEntries();
  }

  async bulkUpdateActivity(ids: number[], activityId: number) {
    await api.put('/api/time-entries/bulk-update-activity', { ids, activityId });
    await this.refreshEntries();
  }

  async bulkToggleBillable(ids: number[]) {
    await api.put('/api/time-entries/bulk-toggle-billable', { ids });
    await this.refreshEntries();
  }
}

export const data = new DataStore();
